import { useState } from "react";
import { Modal, Input, Button, message } from "antd";
import dayjs from "dayjs";
import EventFormBlock from "./EventFormBlock";

const AddEventModal = ({ isOpen, onClose, onSubmit }) => {
  const [mainInfo, setMainInfo] = useState({ name: "", description: "" });
  const [eventList, setEventList] = useState([]);

  const handleAddEventBlock = () => {
    // Only allow adding one event
    if (eventList.length === 0) {
      setEventList([
        {
          id: Date.now(),
          type: null,
          topic: "",
          trainer: "",
          start_date: "",
          end_date: "",
          timing: "",
          link: "",
          location: "",
          courseData: null,
          access_type: "",
          min_participants: "",
          max_participants: "",
          mandatory: "",
        },
      ]);
    }
  };

  const handleEventChange = (id, field, value) => {
    if (field === "timing" && Array.isArray(value)) {
      value = value.map((v) => v.format("hh:mm A")).join(" - ");
    }
    setEventList((prev) =>
      prev.map((ev) => (ev.id === id ? { ...ev, [field]: value } : ev))
    );
  };

  const handleRemoveEventBlock = (id) => {
    setEventList((prev) => prev.filter((ev) => ev.id !== id));
  };

  const validateForm = () => {
    if (!mainInfo.name.trim()) {
      message.error("Event name is required");
      return false;
    }
    if (mainInfo.name.length > 100) {
      message.error("Event name cannot exceed 100 characters");
      return false;
    }
    if (!mainInfo.description.trim()) {
      message.error("Description is required");
      return false;
    }
    if (mainInfo.description.length > 150) {
      message.error("Description cannot exceed 150 characters");
      return false;
    }
    if (eventList.length === 0) {
      message.error("Please add at least one event");
      return false;
    }

    for (let ev of eventList) {
      if (!ev.type) {
        message.error("Event type is required for all events");
        return false;
      }
      if (!ev.topic) {
        message.error("Topic is required for all events");
        return false;
      }
      if (!ev.trainer) {
        message.error("Trainer name is required");
        return false;
      }
      if (!ev.start_date) {
        message.error("Start date is required");
        return false;
      }
      if (!ev.end_date) {
        message.error("End date is required");
        return false;
      }

      const today = dayjs().startOf("day");
      const start = dayjs(ev.start_date);
      const end = dayjs(ev.end_date);

      if (start.isBefore(today)) {
        message.error(
          `Start date cannot be before today for event "${ev.topic}"`
        );
        return false;
      }
      if (end.isBefore(start)) {
        message.error(
          `End date must be after start date for event "${ev.topic}"`
        );
        return false;
      }

      if (!ev.timing) {
        message.error(
          `Start and end time are required for event "${ev.topic}"`
        );
        return false;
      }
      const [startTimeStr, endTimeStr] = ev.timing.split(" - ");
      const startTime = dayjs(startTimeStr, "hh:mm A");
      const endTime = dayjs(endTimeStr, "hh:mm A");
      if (
        !startTime.isValid() ||
        !endTime.isValid() ||
        endTime.isBefore(startTime)
      ) {
        message.error(
          `End time must be after start time for event "${ev.topic}"`
        );
        return false;
      }

      if (ev.type === "virtual" && !ev.link) {
        message.error(`Event link is required for "${ev.topic}"`);
        return false;
      }
      if (ev.type === "ilt" && !ev.location) {
        message.error(`Location is required for offline event "${ev.topic}"`);
        return false;
      }
      if (ev.type === "e-learning" && !ev.courseData) {
        message.error(`Please select a course for "${ev.topic}"`);
        return false;
      }

      if (!ev.access_type) {
        message.error(`Event type is required for "${ev.topic}"`);
        return false;
      }
      if (ev.access_type === "Limited") {
        if (!ev.min_participants || !ev.max_participants) {
          message.error(
            `Min and Max participants are required for "${ev.topic}"`
          );
          return false;
        }
        if (Number(ev.min_participants) > Number(ev.max_participants)) {
          message.error(
            `Min participants cannot be greater than Max for "${ev.topic}"`
          );
          return false;
        }
      }
      if (!ev.mandatory) {
        message.error(`Mandatory field is required for "${ev.topic}"`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const newEvent = {
      mainInfo,
      events: eventList.map((ev) => ({ ...ev, status: "Open" })),
    };
    onSubmit(newEvent);

    onClose();
    setMainInfo({ name: "", description: "" });
    setEventList([]);
  };

  const handleClose = () => {
    setMainInfo({ name: "", description: "" });
    setEventList([]);
    onClose();
  };

  return (
    <Modal
      title="Create Event"
      open={isOpen}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Submit"
      width={600}
    >
      <div className="event-form-container">
        <label className="form-label-first">Event Name</label>
        <Input
          placeholder="Enter event name"
          value={mainInfo.name}
          onChange={(e) => setMainInfo({ ...mainInfo, name: e.target.value })}
          className="form-input"
        />

        <label className="form-label">Description</label>
        <Input.TextArea
          placeholder="Enter description"
          value={mainInfo.description}
          onChange={(e) =>
            setMainInfo({ ...mainInfo, description: e.target.value })
          }
          className="form-textarea"
          rows={3}
        />

        <div className="event-blocks-wrapper">
          {eventList.map((ev, idx) => (
            <EventFormBlock
              key={ev.id}
              event={ev}
              index={idx}
              onChange={handleEventChange}
              onRemove={handleRemoveEventBlock}
            />
          ))}
        </div>

        {/* Only show Add Event button if no event has been added yet */}
        {eventList.length === 0 && (
          <Button onClick={handleAddEventBlock} type="dashed" block>
            + Add Event
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default AddEventModal;